import { memo, useId, useMemo } from 'react';

import { InfoIcon } from '@air/design-icons';
import { Tooltip } from '@air/primitive-tooltip';

import { WorkspaceAgentGuidedVariantNumberUnitField } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/WorkspaceAgentGuidedVariantNumberUnitField';
import {
  WORKSPACE_AGENT_WATERMARK_LIMITS,
  type WorkspaceAgentWatermarkMarginUnit,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentWatermark';

export type WorkspaceAgentGuidedVariantWatermarkMarginFieldProps = {
  disabled: boolean;
  margin: number;
  marginPxMax: number | null;
  marginUnit: WorkspaceAgentWatermarkMarginUnit;
  onMarginChange: (margin: number) => void;
  onMarginUnitChange: (unit: WorkspaceAgentWatermarkMarginUnit) => void;
};

export const WorkspaceAgentGuidedVariantWatermarkMarginField = memo(
  ({
    disabled,
    margin,
    marginPxMax,
    marginUnit,
    onMarginChange,
    onMarginUnitChange,
  }: WorkspaceAgentGuidedVariantWatermarkMarginFieldProps) => {
    const hintId = useId();
    const inputId = useId();
    const max = useMemo(
      () => ({
        percent: WORKSPACE_AGENT_WATERMARK_LIMITS.marginPercentMax,
        px: marginPxMax,
      }),
      [marginPxMax],
    );

    return (
      <div className="flex min-w-0 flex-col gap-1.5">
        <div className="flex items-center gap-1">
          <label className="ads:text-primary text-12 text-gray-12" htmlFor={inputId}>
            Margin
          </label>
          <Tooltip className="max-w-[240px] font-normal text-12" label="% scales with each image; px stays fixed.">
            <button
              aria-describedby={hintId}
              aria-label="About margin units"
              className="inline-flex items-center"
              type="button"
            >
              <InfoIcon aria-hidden className="ads:text-icon-default text-gray-10" size="xs" />
            </button>
          </Tooltip>
          <span className="sr-only" id={hintId}>
            % scales with each image; px stays fixed.
          </span>
        </div>
        <WorkspaceAgentGuidedVariantNumberUnitField
          aria-describedby={hintId}
          disabled={disabled}
          id={inputId}
          max={max}
          min={0}
          onUnitChange={onMarginUnitChange}
          onValueChange={onMarginChange}
          unit={marginUnit}
          value={margin}
        />
      </div>
    );
  },
);

WorkspaceAgentGuidedVariantWatermarkMarginField.displayName = 'WorkspaceAgentGuidedVariantWatermarkMarginField';
